<template>
    <v-layout column class="justify-space-between">
        <div v-if="customerHangup" class="pa-3">
            <div class="text-h6 mb-3">Video Call Ended</div>
            The customer has ended the video call. When you are ready, please disposition the video call.
            <v-btn
                data-testid="hangup-end-call-btn"
                class="hangup-end-call-btn error mt-3"
                block
                @click="endCall"
            >
                Close
            </v-btn>
        </div>
        <v-layout style="height:92vh" column class="align-center justify-space-between" v-else>
            <v-flex v-if="loading" class="pa-3">
                <v-layout justify-center>
                    <v-progress-circular
                        :size="100"
                        :width="7"
                        color="primary"
                        indeterminate
                    />
                </v-layout>
                <div class="mt-5 subheading">Entering the video call room...</div>
            </v-flex>
            <v-flex class="pa-3" v-show="!previewMode && !loading">
                <v-card style="text-align: center">
                    <div id="remoteTrack" />
                </v-card>
                <div v-show="!activeParticipants">Video call room is currently empty.</div>
                <v-btn
                    data-testid="end-video-call-btn"
                    class="end-video-call-btn error my-3"
                    block
                    @click="endCall"
                >
                    End Video Call
                </v-btn>
                <chat-logs />
            </v-flex>
            <v-flex class="pa-3 shrink">
                <v-card style="text-align: center" width="268px">
                    <video ref="video" width="250" height="188" autoplay></video>
                </v-card>
                <div v-show="previewMode">
                    <div class="my-3">
                        <div class="subheading font-weight-bold mb-2">Agent Preview</div>
                        This is how you will appear to the customer. When you are ready, click "Enter Chat Room"
                        to begin the video call.
                    </div>
                    <v-btn
                        data-testid="enter-chat-room-btn"
                        class="enter-chat-room-btn"
                        color="primary"
                        block
                        @click="initializeChat"
                    >
                        Enter Chat Room
                    </v-btn>
                </div>
            </v-flex>
        </v-layout>
    </v-layout>
</template>

<script>
/* eslint-disable no-return-await */
/* eslint-disable no-undef */
import ChatLogs from "@/components/ChatLogs.vue";
import { EventBus } from "@/Event";
import Twilio from "twilio-video";
import axios from "axios";

export default {
  data() {
    return {
      activeRoom: "",
      activeParticipants: false,
      customerHangup: false,
      data: {},
      identity: "",
      loading: false,
      previewMode: true,
      pusher: null,
      remoteTrack: "",
      roomName: null,
      video: ""
    };
  },

  methods: {
    async getAccessToken() {
      return axios.post(process.env.VUE_APP_API_TWILIO + `/identity`,
        { identity: this.me.first_name }
      );
    },

    // Trigger log events
    dispatchLog(message) {
      EventBus.$emit("newLog", message);
    },

    // Attach the Tracks to the DOM.
    attachTracks(tracks, container) {
      tracks.forEach(track => container.appendChild(track.attach()));
    },

    // Detach a Track from the DOM.
    detachTrack(track) {
      track.detach().forEach(element => element.remove());
    },

    // Detach the Participant's Tracks from the DOM.
    detachParticipantTracks(participant) {
      let tracks = this.getTracks(participant);
      tracks.forEach(track => this.detachTrack(track));
    },

    // Get Tracks
    getTracks(participant) {
      return Array.from(participant.tracks.values()).filter(publication => {
        return !publication.isSubscribed;
      }).map(publication => publication.track);
    },

    // Leave Room.
    leaveRoomIfJoined() {
      if (this.activeRoom) {
        this.activeRoom.disconnect();
      }
    },

    // Handle Track
    handleTrack(track) {
      switch (track.kind) {
        case "audio":
          document.getElementById("remoteTrack").appendChild(track.attach());
          break;

        case "video":
          if (track.name.split("__")[1] === "self") {
            document.getElementById("remoteTrack").appendChild(track.attach());
          }
          break;

        case "data":
          break;
      }
    },

    // End call
    endCall() {
      this.leaveRoomIfJoined();
      this.$store.commit("endTwilioChat");
      this.video.pause();
      this.video.src = "";
      if (this.video.srcObject) this.video.srcObject.getTracks()[0].stop();
    },

    // Create the Twilio instance when the agent is ready
    initializeChat() {
      this.createChat();
      this.previewMode = false;
      // When a user is about to transition away from this page,
      // disconnect from the room, if joined.
      window.addEventListener("beforeunload", this.leaveRoomIfJoined);
    },

    createChat() {
      this.loading = true;

      this.getAccessToken().then((data) => {
        this.roomName = null;
        const roomName = this.$store.state.call.video_room_name;
        const token = data.data.token;
        let connectOptions = {
          name: roomName,
          // logLevel: "debug",
          audio: true,
          video: { width: 575 }
        };

        // before a user enters a new room,
        // disconnect the user from they joined already
        this.leaveRoomIfJoined();

        // remove any remote track when joining a new room
        let remoteTrack = document.getElementById("remoteTrack");
        remoteTrack.innerHTML = "";

        Twilio.connect(token, connectOptions).then(room => {
          this.dispatchLog("You have successfully joined the Video Call (call id: " + roomName + ").");
          this.dispatchLog("You are connected with " + this.videoCustomer + ".");

          // set active room
          this.activeRoom = room;
          this.roomName = roomName;
          this.loading = false;

          // Attach the Tracks of the Room's Participants.
          room.participants.forEach(participant => {
            participant.tracks.forEach(publication => {
              if (publication.isSubscribed) {
                const track = publication.track;
                remoteTrack.appendChild(track.attach());
                this.activeParticipants = true;
              }
            });
          });

          // When a Participant joins the Room, log the event.
          room.on("participantConnected", participant => {
            this.dispatchLog(participant.identity + " has joined the room.");
            participant.tracks.forEach((publication) => {
              publication.on("subscribed", (track) => {
                this.handleTrack(track);
                this.activeParticipants = true;
              });
              publication.on("unsubscribed", (track) => {
                this.detachTrack(track);
                this.activeParticipants = false;
              });
            });
          });

          room.on("participantDisconnected", participant => {
            this.dispatchLog(participant.identity + " has left the room.");
            participant.tracks.forEach(track => track.detach());
            this.activeParticipants = false;
          });

          // When a Participant adds a Track, attach it to the DOM.
          room.on("trackSubscribed", track => {
            this.attachTracks([track], remoteTrack);
            this.activeParticipants = true;
          });

          // When a Participant removes a Track, detach it from the DOM.
          room.on("unsubscribed", track => {
            this.detachTrack(track);
          });

          // When a Participant leaves the Room, detach its Tracks.
          room.on("trackUnpublished", participant => {
            this.dispatchLog(participant.identity + " has left the room.");
            this.detachParticipantTracks(participant);
          });

          // if local preview is not active, create it
          // if(this.previewMode) {
          //     createLocalVideoTrack().then((track) => {
          //         let localMediaContainer = document.getElementById("localTrack");
          //
          //         // if (localMediaContainer) localMediaContainer.appendChild(track.attach());
          //         localMediaContainer.appendChild(track.attach());
          //         this.previewMode = true;
          //     });
          // }
        });
      });
    },

    videoCallCancelled() {
      this.customerHangup = true;
    },

    subscribeToPusher() {
      if (!this.pusher) {
        this.pusher = new Pusher(process.env.VUE_APP_PUSHER_KEY, {
          cluster: "us2",
          forceTLS: true,
          authEndpoint: process.env.VUE_APP_API_HOST + "/auth/pusher",
          auth: {
            headers: {
              "Authorization": sessionStorage.getItem("access_token")
            }
          }
        });
      }

      const videoChannel = this.pusher.subscribe("private-video-call");
      videoChannel.bind("App\\Events\\VideoCallCancelled", () => {
        this.videoCallCancelled();
      });
    },

    unsubscribeToPusher() {
      this.pusher.disconnect();
      this.pusher = null;
    }
  },

  computed: {
    videoCustomer() {
      return this.$store.state.call.video_customer;
    },

    me() {
      return this.$store.state.user.me;
    }
  },

  created() {
    this.subscribeToPusher();
  },

  mounted() {
    this.video = this.$refs.video;
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then((stream) => {
          this.video.srcObject = stream;
          this.video.play();
        });
    }
  },

  beforeDestroy() {
    this.unsubscribeToPusher();
    if (this.activeRoom) this.endCall();
  },

  components: {
    ChatLogs
  }
};
</script>

<style scoped>

</style>
